// SignalR client for the TestUi events hub. Listens for OnWebhookCaptured and accumulates
// captures into a state array, most-recent-first. Initial backfill via REST so the UI
// has data on first paint even if no webhook fires while the user is watching.

import { useEffect, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import type { WebhookCaptureDto } from "../api/types";
import { URLS } from "../api/client";
import { recentWebhooks } from "../api/alerts";

const MAX_CAPTURES = 100;

export interface TestEventsState {
  connected: boolean;
  captures: WebhookCaptureDto[];
}

export function useTestEvents(): TestEventsState {
  const [connected, setConnected] = useState(false);
  const [captures, setCaptures] = useState<WebhookCaptureDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${URLS.testUi}/hubs/test-events`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("OnWebhookCaptured", (capture: WebhookCaptureDto) => {
      if (cancelled) return;
      setCaptures((prev) => {
        const next = [capture, ...prev];
        // Cap to keep memory bounded for long sessions.
        return next.length > MAX_CAPTURES ? next.slice(0, MAX_CAPTURES) : next;
      });
    });

    (async () => {
      try {
        await connection.start();
        if (cancelled) return;
        setConnected(true);
        // Backfill with the buffer's current contents so a refresh doesn't lose history.
        // MERGE (not replace): a live capture can arrive between start() resolving and the REST
        // backfill completing; a wholesale replace would drop it. Dedupe by receivedAt+fingerprint.
        try {
          const backfill = await recentWebhooks();
          if (!cancelled) {
            setCaptures((live) => {
              const keyOf = (c: WebhookCaptureDto) =>
                `${c.receivedAt}|${c.headers?.["X-Webhook-Fingerprint"] ?? ""}`;
              const seen = new Set<string>();
              const merged: WebhookCaptureDto[] = [];
              for (const c of [...live, ...backfill]) {
                const k = keyOf(c);
                if (seen.has(k)) continue;
                seen.add(k);
                merged.push(c);
              }
              merged.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt)); // most-recent-first
              return merged.length > MAX_CAPTURES ? merged.slice(0, MAX_CAPTURES) : merged;
            });
          }
        } catch {
          // not fatal — live stream still works
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("TestEvents connection failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      setConnected(false);
      connection.stop().catch(() => undefined);
    };
  }, []);

  return { connected, captures };
}
